<script>
  import { createEventDispatcher, tick } from 'svelte';
  import dayjs from 'dayjs';

  import { Checkbox, Column, DataTable, Grid, InlineNotification, Modal, Row, Search, Tag, TextInput } from 'carbon-components-svelte';
  import sortBy from 'lodash/sortBy';
  import uniq from 'lodash/uniq';

  import { getFirstDayOfTheMonth, getLastDayOfTheMonth, getNextWorkdayAfter } from '../../../../../shared/convert-dates';

  import { validateExpiryDate, validateDateInput } from '../../../../domains/entitlements/utils/entitlements-date-validation';

  export let bulkAddModalOpen, feedInventory;

  const dispatch = createEventDispatcher();
  const EXPIRY_DATE_DEFAULT_AHEAD_DAYS = 14;
  const defaultValues = { startDate: '', endDate: '', isOngoing: false, isDayEntitlement: false, expiryDate: '' };

  let addedFeeds = [],
    values = { ...defaultValues },
    noOverlapErrors = [],
    searchData = '',
    totalAddedFeeds = 0;

  const convertDate = (date) => date.slice(0, values.isDayEntitlement ? 10 : 7);

  const onSearchTextChange = ({ target }) => {
    searchData = target?.value?.trim() ?? '';
  };

  const refreshDates = async () => {
    await tick();
    if (
      values.isDayEntitlement &&
      (!values.startDate || values.startDate < addedFeedsStartAvailability || values.startDate > addedFeedsEndAvailability)
    ) {
      values.startDate = addedFeedsStartAvailability;
      values.endDate = addedFeedsStartAvailability;
      return;
    }
    if (!values.startDate || values.startDate < addedFeedsStartAvailability || values.startDate > addedFeedsEndAvailability) {
      values.startDate = addedFeedsStartAvailability.slice(0, 7);
    }
    if (!values.endDate || values.endDate > addedFeedsEndAvailability || values.endDate < addedFeedsStartAvailability) {
      values.endDate = addedFeedsEndAvailability.slice(0, 7);
    }
  };

  const handleAddFeed = async ({
    id,
    name,
    availabilityStartDate = dayjs('2000-01-01').format('YYYY-MM-DD'),
    availabilityEndDate = dayjs().format('YYYY-MM-DD'),
    availabilityEndMonth = dayjs().format('YYYY-MM'),
    missingDays = [],
    missingMonths = [],
  }) => {
    if (addedFeeds.length && (availabilityStartDate > addedFeedsEndAvailability || availabilityEndDate < addedFeedsStartAvailability)) {
      noOverlapErrors = [{ name }, ...noOverlapErrors.slice(0, 1)];
      return;
    }
    addedFeeds = [
      ...addedFeeds,
      { id, name, startDate: availabilityStartDate, endDate: availabilityEndDate, availabilityEndMonth, missingDays, missingMonths },
    ];
    await refreshDates();
  };

  const handleDeleteFeed = (name) => {
    addedFeeds = addedFeeds.filter((addedFeed) => addedFeed.name !== name);
  };

  const handleSubmitFeedModal = (shouldClose = true) => {
    if (Object.values(validationErrors).some(Boolean)) {
      return;
    }

    dispatch('add-feeds', {
      feeds: addedFeeds.map(({ id, name }) => ({
        id,
        name,
        availabilityStartDate: values.startDate,
        availabilityEndDate: values.endDate,
        isOngoing: values.isOngoing,
        isDayEntitlement: values.isDayEntitlement,
        expiryDate: values.expiryDate,
        isEdited: true,
      })),
      options: { persistSelectedRanges: true },
    });

    totalAddedFeeds += addedFeeds.length;
    values = { ...defaultValues };

    if (shouldClose) {
      handleCloseModal();
    }
  };

  const setAndValidateKeyDate = (value, key) => {
    values[key] = value;
  };

  const handleToggleIsOngoing = (event) => {
    event.preventDefault();
    if (values.isDayEntitlement) {
      return;
    }
    values.isOngoing = !values.isOngoing;
  };

  const handleToggleDayEntitlement = (event) => {
    event.preventDefault();
    if (values.isDayEntitlement) {
      values.isDayEntitlement = false;
      values.expiryDate = null;
      values.startDate = addedFeedsLatestStartDate?.slice(0, 7);
      values.endDate = addedFeedsEarliestEndDate?.slice(0, 7);
    } else {
      values.isDayEntitlement = true;
      values.expiryDate = getNextWorkdayAfter(EXPIRY_DATE_DEFAULT_AHEAD_DAYS);
      values.isOngoing = false;
      values.startDate = addedFeedsLatestStartDate;
      values.endDate = addedFeedsLatestStartDate;
    }
  };

  const handleExpiryDateChange = ({ target: { value } }) => {
    values.expiryDate = value;
  };

  const generalizedValidateDateInput = (key, value) => {
    const { missingDays, missingMonths } = addedFeeds.reduce((obj, curr) => {
      return {
        missingDays: uniq([...(obj.missingDays || []), ...curr.missingDays]),
        missingMonths: uniq([...(obj.missingMonths || []), ...curr.missingMonths]),
      };
    }, {});
    const oppositeDateKey = key === 'startDate' ? 'endDate' : 'startDate';
    return validateDateInput({
      value,
      availableStartDate: new Date(addedFeedsStartAvailability),
      availableEndDate: new Date(addedFeedsEndAvailability),
      isDayEntitlement: values.isDayEntitlement,
      key,
      oppositeDate: values[oppositeDateKey],
      missingDays,
      missingMonths,
    });
  };

  const handleSecondaryButtonsClick = ({ detail: { text } }) => {
    switch (text) {
      case 'Close':
        handleCloseModal();
        break;
      case 'Add':
        handleSubmitFeedModal(false);
        break;
    }
  };

  const handleCloseModal = () => {
    bulkAddModalOpen = false;
    addedFeeds = [];
    values = { ...defaultValues };
    noOverlapErrors = [];
    searchData = '';
    totalAddedFeeds = 0;
  };

  $: addableFeeds = feedInventory.filter(({ name }) => !addedFeeds.some(({ name: addedFeedName }) => addedFeedName === name));
  $: filteredAddableFeeds = searchData
    ? addableFeeds?.filter(({ name }) => name.toLocaleLowerCase().includes(searchData.toLocaleLowerCase()))
    : addableFeeds;
  $: sortedAddedFeeds = sortBy(addedFeeds, 'name');
  $: sortedFeedsByStartDate = sortBy(addedFeeds, 'startDate');
  $: sortedFeedsByEndDate = sortBy(addedFeeds, 'endDate');
  $: sortedFeedsByEndMonth = sortBy(addedFeeds, 'availabilityEndMonth');
  $: addedFeedsLatestStartDate = sortedFeedsByStartDate[sortedFeedsByStartDate.length - 1]?.startDate ?? '';
  $: addedFeedsEarliestEndDate = sortedFeedsByEndDate[0]?.endDate ?? '';
  $: addedFeedsEarliestEndMonth = sortedFeedsByEndMonth[0]?.availabilityEndMonth ?? '';

  $: addedFeedsStartAvailability = values.isDayEntitlement
    ? addedFeedsLatestStartDate
    : addedFeedsLatestStartDate && getFirstDayOfTheMonth(addedFeedsLatestStartDate?.slice(0, 7));

  $: addedFeedsEndAvailability = values.isDayEntitlement
    ? addedFeedsEarliestEndDate
    : addedFeedsEarliestEndMonth && getLastDayOfTheMonth(addedFeedsEarliestEndMonth);

  // validationErrors must be computed after other computed values
  $: validationErrors = {
    startDate: addedFeeds.length && generalizedValidateDateInput('startDate', values.startDate),
    endDate: addedFeeds.length && generalizedValidateDateInput('endDate', values.endDate),
    expiryDate: values.expiryDate && validateExpiryDate(values.expiryDate),
  };
</script>

<Modal
  modalHeading="Bulk Add Feeds"
  preventCloseOnClickOutside={true}
  primaryButtonText="Add & Close"
  secondaryButtons={[{ text: 'Close' }, { text: 'Add' }]}
  size="lg"
  bind:open={bulkAddModalOpen}
  on:click:button--secondary={handleSecondaryButtonsClick}
  on:close={handleCloseModal}
  on:submit={handleSubmitFeedModal}
>
  <div class="errors-wrapper">
    {#each noOverlapErrors as { name }}
      <InlineNotification
        kind="warning"
        lowContrast
        title="Warning:"
        subtitle={`Feed ${name} cannot be added because it has no overlap with the current feed set.`}
      />
    {/each}
  </div>
  <div class="added-feeds-wrapper">
    <Grid fullWidth>
      <Row>
        <Column sm={4} md={4} lg={8}>
          <div class="section-wrapper" role="group" aria-label="addable feeds">
            <Search
              labelText="Search feeds..."
              persistent
              size="sm"
              value={searchData}
              on:clear={onSearchTextChange}
              on:input={onSearchTextChange}
            />

            <div class="tile-wrapper">
              <DataTable
                size="short"
                headers={[{ key: 'name', value: 'Inventory' }]}
                rows={filteredAddableFeeds}
                virtualize={{ containerHeight: 333 }}
                on:click:row={({ detail }) => handleAddFeed(detail.row)}
              />
            </div>
          </div>
        </Column>
        <Column sm={4} md={4} lg={8}>
          <div class="section-wrapper" role="group" aria-label="added feeds">
            {#if !addedFeeds.length}
              <em class="note">No feeds selected yet</em>
            {:else}
              <div class="added-tile-wrapper">
                <DataTable
                  size="short"
                  headers={[{ key: 'name', value: 'Selected Feeds' }]}
                  rows={sortedAddedFeeds}
                  virtualize={{ containerHeight: 365 }}
                  on:click:row={({ detail }) => handleDeleteFeed(detail.row.name)}
                />
              </div>
            {/if}
          </div>
        </Column>
      </Row>
    </Grid>
  </div>

  <div class="settings-wrapper">
    {#if addedFeedsStartAvailability}
      <div class="feed-availability">
        The current selection is available from {convertDate(addedFeedsStartAvailability)} to {convertDate(addedFeedsEndAvailability)}
      </div>
    {/if}
    <div class="input-checkbox-wrapper">
      <TextInput
        name="startDate"
        labelText="Start Date"
        placeholder="Start date"
        required
        size="sm"
        invalid={validationErrors.startDate}
        invalidText={validationErrors.startDate}
        value={values.startDate}
        on:blur={(e) => setAndValidateKeyDate(e.target.value, 'startDate')}
      />
      <TextInput
        name="endDate"
        labelText="End Date"
        placeholder="End date"
        required
        size="sm"
        invalid={validationErrors.endDate}
        invalidText={validationErrors.endDate}
        value={values.endDate}
        on:blur={(e) => setAndValidateKeyDate(e.target.value, 'endDate')}
      />
      <Checkbox labelText="Ongoing" checked={values.isOngoing} on:click={handleToggleIsOngoing} disabled={values.isDayEntitlement} />
    </div>
    <div class="input-checkbox-wrapper">
      <TextInput
        invalid={validationErrors.expiryDate}
        invalidText={validationErrors.expiryDate}
        labelText="Expiry date"
        placeholder="yyyy-mm-dd"
        disabled={!values.isDayEntitlement}
        size="sm"
        value={values.expiryDate}
        on:blur={handleExpiryDateChange}
      />
      <Checkbox labelText="Day Entitlement" checked={values.isDayEntitlement} on:click={handleToggleDayEntitlement} />
    </div>
  </div>
  {#if totalAddedFeeds}
    <div class="info">
      <Tag type="green">{totalAddedFeeds} feed{totalAddedFeeds === 1 ? '' : 's'} added to main page so far</Tag>
    </div>
  {/if}
</Modal>

<style>
  .errors-wrapper {
    margin: 0 2rem;
  }

  .added-feeds-wrapper {
    margin-bottom: 2rem;
  }

  .tile-wrapper {
    margin-bottom: 1rem;
  }

  .section-wrapper :global(.bx--data-table tbody tr) {
    cursor: pointer;
  }

  .section-wrapper :global(svg) {
    margin-right: 0.5rem;
  }

  .settings-wrapper {
    margin: 0 2rem;
  }

  .feed-availability {
    margin-bottom: 1rem;
  }

  .settings-wrapper :global(.bx--form-item) {
    margin-bottom: 0.5rem;
  }

  .settings-wrapper :global(.bx--text-input:disabled) {
    background-color: var(--cds-field-02, #c6c6c6);
  }

  .input-checkbox-wrapper {
    display: flex;
    align-items: flex-end;
    gap: 0.5rem;
  }

  .input-checkbox-wrapper :global(.bx--text-input-wrapper) {
    flex-grow: 2;
  }

  .info {
    position: fixed;
    bottom: 1rem;
  }

  .note {
    font-size: 12px;
    color: var(--cds-text-helper);
  }
</style>
